class CyclingBanner {
  constructor(element) {
    if (element.cyclingBanner) return element.cyclingBanner;

    this.container = element;
    this.container.cyclingBanner = this;
    this.items = Array.from(element.querySelectorAll('.banner__media--cycling'));
    this.totalItems = this.items.length;
    this.currentIndex = Math.max(0, this.items.findIndex((item) => item.classList.contains('active')));
    this.autoPlay = element.dataset.autoCycle === 'true';
    this.media = window.matchMedia('(min-width: 750px)');
    this.interval = Math.max(parseInt(element.dataset.cycleSpeed, 10) || 5000, 2000);
    this.timer = null;
    this.transitionTimer = null;
    this.transitionToken = 0;

    if (!this.totalItems) return;

    this.items.forEach((item, index) => {
      const active = index === this.currentIndex;
      item.classList.toggle('active', active);
      item.classList.remove('is-next');
      item.setAttribute('aria-hidden', String(!active));
    });

    this.container.dataset.cyclingReady = 'true';
    if (this.media.matches) this.preload(this.currentIndex + 1);

    this.onMediaChange = this.handleMediaChange.bind(this);
    if (this.media.addEventListener) this.media.addEventListener('change', this.onMediaChange);
    else if (this.media.addListener) this.media.addListener(this.onMediaChange);

    if (this.autoPlay && this.totalItems > 1 && this.media.matches) this.start();
  }

  preload(index) {
    if (!this.totalItems) return;
    const item = this.items[((index % this.totalItems) + this.totalItems) % this.totalItems];
    const image = item?.querySelector('img');
    if (!image) return;
    image.loading = 'eager';
    if (typeof image.decode === 'function') image.decode().catch(() => {});
  }

  waitForImage(item) {
    const image = item?.querySelector('img');
    if (!image) return Promise.resolve();

    image.loading = 'eager';
    if (image.complete && image.naturalWidth > 0) {
      return typeof image.decode === 'function' ? image.decode().catch(() => {}) : Promise.resolve();
    }

    return new Promise((resolve) => {
      const done = () => {
        if (typeof image.decode === 'function') image.decode().catch(() => {}).finally(resolve);
        else resolve();
      };
      image.addEventListener('load', done, { once: true });
      image.addEventListener('error', resolve, { once: true });
    });
  }

  async show(index) {
    if (this.totalItems <= 1) return;

    const nextIndex = ((index % this.totalItems) + this.totalItems) % this.totalItems;
    if (nextIndex === this.currentIndex) return;

    const token = ++this.transitionToken;
    const currentItem = this.items[this.currentIndex];
    const nextItem = this.items[nextIndex];

    await this.waitForImage(nextItem);
    if (token !== this.transitionToken) return;

    nextItem.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => {
      if (token !== this.transitionToken) return;
      nextItem.classList.add('is-next');

      clearTimeout(this.transitionTimer);
      this.transitionTimer = setTimeout(() => {
        if (token !== this.transitionToken) return;
        currentItem.classList.remove('active');
        currentItem.classList.remove('is-next');
        currentItem.setAttribute('aria-hidden', 'true');
        nextItem.classList.add('active');
        nextItem.classList.remove('is-next');
        this.currentIndex = nextIndex;
        this.preload(this.currentIndex + 1);
      }, 220);
    });
  }

  next() {
    return this.show(this.currentIndex + 1);
  }

  start() {
    this.stop();
    if (!this.media.matches || !this.autoPlay || this.totalItems <= 1) return;
    this.timer = setInterval(() => this.next(), this.interval);
  }

  handleMediaChange() {
    this.transitionToken += 1;
    clearTimeout(this.transitionTimer);
    this.stop();

    if (!this.media.matches) {
      this.currentIndex = 0;
      this.items.forEach((item, index) => {
        const active = index === 0;
        item.classList.toggle('active', active);
        item.classList.remove('is-next');
        item.setAttribute('aria-hidden', String(!active));
      });
      return;
    }

    this.preload(this.currentIndex + 1);
    this.start();
  }

  stop() {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  destroy() {
    this.stop();
    clearTimeout(this.transitionTimer);
    this.transitionToken += 1;
    if (this.media.removeEventListener) this.media.removeEventListener('change', this.onMediaChange);
    else if (this.media.removeListener) this.media.removeListener(this.onMediaChange);
    delete this.container.cyclingBanner;
  }
}

function initCyclingBanners(scope = document) {
  scope.querySelectorAll('.banner--cycling').forEach((banner) => new CyclingBanner(banner));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initCyclingBanners());
} else {
  initCyclingBanners();
}

document.addEventListener('shopify:section:load', (event) => initCyclingBanners(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('.banner--cycling').forEach((banner) => banner.cyclingBanner?.destroy());
});
